/*
 * Detail Plus: estimator + quote hand-off.
 *
 * Every number comes from window.DP.pricing (theme settings, Theme settings > Detail Plus).
 * A price of 0 means "not confirmed": the estimator shows the spec with no dollar figure.
 * Buttons with [data-dp-quote] open the Tally form in a popup with hidden fields filled.
 */
(function () {
  'use strict';

  var DP = window.DP || {};
  var P = DP.pricing || {};

  function money(n) {
    return '$' + Math.round(n).toLocaleString('en-US');
  }
  function range(lo, hi) {
    return lo === hi ? money(lo) : money(lo) + ' to ' + money(hi);
  }
  function num(v, fallback) {
    var n = parseFloat(v);
    return isFinite(n) && n > 0 ? n : fallback;
  }

  /* ---------------- Tally hand-off ---------------- */

  var tallyLoading = null;
  function loadTally() {
    if (window.Tally) return Promise.resolve(window.Tally);
    if (tallyLoading) return tallyLoading;
    tallyLoading = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = 'https://tally.so/widgets/embed.js';
      s.async = true;
      s.onload = function () { resolve(window.Tally); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return tallyLoading;
  }

  function openQuote(fields) {
    var formId = (DP.tallyFormId || '').trim();
    var hidden = Object.assign({ page_url: window.location.href }, fields || {});
    if (!formId) {
      window.location.href = DP.fallbackUrl || '/pages/contact';
      return;
    }
    loadTally()
      .then(function (Tally) {
        Tally.openPopup(formId, {
          layout: 'modal',
          width: 720,
          overlay: true,
          hiddenFields: hidden
        });
      })
      .catch(function () {
        // Script blocked or offline: open the hosted form in the same tab, answers in the query string.
        var q = Object.keys(hidden)
          .map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(hidden[k]); })
          .join('&');
        window.location.href = 'https://tally.so/r/' + formId + '?' + q;
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-dp-quote]');
    if (!btn) return;
    // Estimator buttons are handled by their estimator.
    if (btn.closest('[data-dp-estimator]')) return;
    e.preventDefault();
    openQuote({ product: btn.getAttribute('data-dp-quote') || '', source: btn.getAttribute('data-dp-source') || 'button' });
  });

  /* ---------------- Estimator ---------------- */

  function Estimator(root) {
    this.root = root;
    this.out = {
      est: root.querySelector('[data-dp-est]'),
      sub: root.querySelector('[data-dp-est-sub]'),
      spec: root.querySelector('[data-dp-spec]')
    };
    var self = this;
    root.addEventListener('input', function () { self.render(); });
    root.addEventListener('change', function () { self.render(); });
    root.querySelector('[data-dp-send]').addEventListener('click', function (e) {
      e.preventDefault();
      self.send();
    });
    this.render();
  }

  Estimator.prototype.val = function (name) {
    var el = this.root.querySelector('[name="' + name + '"]:checked') || this.root.querySelector('[name="' + name + '"]:not([type="radio"]):not([type="checkbox"])');
    return el ? el.value : '';
  };

  Estimator.prototype.lines = function () {
    var picked = [];
    this.root.querySelectorAll('[name="dp-line"]:checked').forEach(function (el) {
      picked.push(el.value);
    });
    return picked;
  };

  Estimator.prototype.calc = function () {
    var lines = this.lines();
    var groups = [];
    var lo = 0, hi = 0, priced = 0, unpriced = 0;

    if (lines.indexOf('mat') > -1) {
      var surface = this.val('dp-surface') || 'indoor';
      var size = this.val('dp-mat-size') || 'standard';
      var rows = [];
      var matPrice = num(P.matStandardPrice, 0);
      var standardIndoor = size === 'standard' && surface === 'indoor';
      if (size === 'standard') rows.push(['Size', P.matStandardSize || 'Standard']);
      else {
        var w = num(this.val('dp-mat-w'), 0), l = num(this.val('dp-mat-l'), 0);
        rows.push(['Size', w && l ? w + ' x ' + l + ' ft (' + (w * l).toLocaleString('en-US') + ' sq ft)' : 'Custom, tell us the area']);
      }
      if (surface === 'outdoor') {
        rows.push(['Material', '40 mil vinyl']);
        rows.push(['Underlayment', 'Included for rough ground']);
      } else if (surface === 'mobile') {
        rows.push(['Use', 'Mobile, moves between jobs']);
        rows.push(['Material', 'Confirmed with you']);
      } else {
        rows.push(['Material', 'Lighter vinyl for smooth concrete']);
        rows.push(['Underlayment', 'Not needed']);
      }
      if (standardIndoor && matPrice) {
        rows.push(['Price', money(matPrice) + (P.matIncludesShipping ? ' shipped' : '')]);
        lo += matPrice; hi += matPrice; priced++;
      } else {
        rows.push(['Price', 'Quoted to your surface and size']);
        unpriced++;
      }
      groups.push({ title: 'Water containment mat', rows: rows });
    }

    if (lines.indexOf('curtain') > -1) {
      var height = num(this.val('dp-curtain-h'), 0);
      var run = num(this.val('dp-curtain-run'), 0);
      var cRows = [];
      cRows.push(['Height', height ? height + ' ft' : 'Tell us the height']);
      cRows.push(['Curtain run', run ? run + ' linear ft' : 'Tell us the length']);
      var fast = num(P.curtainFtHigh, 0), slow = num(P.curtainFtLow, 0);
      if (run && fast && slow) {
        var dLo = Math.ceil(run / fast), dHi = Math.ceil(run / slow);
        cRows.push(['Install time', dLo === dHi ? 'About ' + dLo + (dLo === 1 ? ' day' : ' days') : 'About ' + dLo + ' to ' + dHi + ' days']);
      }
      cRows.push(['Color and panel style', 'Your choice, no effect on price']);
      var cLo = num(P.curtainLow, 0), cHi = num(P.curtainHigh, 0);
      if (cLo && cHi) {
        cRows.push(['Most curtain projects', range(cLo, cHi)]);
      }
      // Curtains are always quoted to the measured run; the typical range is context, not an estimate.
      unpriced++;
      groups.push({ title: 'Wash bay curtains', rows: cRows, typical: cLo && cHi ? [cLo, cHi] : null });
    }

    if (lines.indexOf('system') > -1) {
      var bays = parseInt(this.val('dp-bays'), 10) || 1;
      var stations = bays; // TODO-KEITH (follow-up Q23): confirm how station count maps to bays.
      var sLo = num(P.stationLow, 0), sHi = num(P.stationHigh, 0);
      var sRows = [['Bays', bays === 6 ? '6 or more' : bays], ['Stations', stations], ['Each station', 'Chemical delivery, air and vacuum']];
      if (sLo && sHi) {
        sRows.push(['Station cost', range(stations * sLo, stations * sHi)]);
        lo += stations * sLo; hi += stations * sHi; priced++;
      } else {
        unpriced++;
      }
      groups.push({ title: 'Detail stations', rows: sRows });
    }

    var project = this.val('dp-project');
    return { lines: lines, groups: groups, lo: lo, hi: hi, priced: priced, unpriced: unpriced, project: project };
  };

  Estimator.prototype.render = function () {
    var r = this.calc();
    var root = this.root;

    root.querySelectorAll('[data-dp-when]').forEach(function (el) {
      el.hidden = r.lines.indexOf(el.getAttribute('data-dp-when')) === -1;
    });
    var customWrap = root.querySelector('[data-dp-mat-custom]');
    if (customWrap) customWrap.hidden = this.val('dp-mat-size') !== 'custom';

    var est = this.out.est, sub = this.out.sub;
    est.classList.remove('dp-est--none');
    if (!r.lines.length) {
      est.textContent = 'Pick what you need';
      est.classList.add('dp-est--none');
      sub.textContent = 'Choose what you need to see a range.';
    } else if (r.priced) {
      est.textContent = range(r.lo, r.hi);
      sub.textContent = r.unpriced ? 'Starting range, plus items we price from your measurements' : 'Starting range from what you told us';
    } else {
      var only = r.groups.length === 1 && r.groups[0].typical;
      if (only) {
        est.textContent = range(only[0], only[1]);
        sub.textContent = 'What most curtain projects run. Yours is priced from your measurements.';
      } else {
        est.textContent = 'Priced to your specs';
        est.classList.add('dp-est--none');
        sub.textContent = 'Send the details and a person prices it from your measurements.';
      }
    }

    var html = '';
    r.groups.forEach(function (g) {
      html += '<li class="dp-spec__group"><span>' + g.title + '</span><span></span></li>';
      g.rows.forEach(function (row) {
        html += '<li><span>' + row[0] + '</span><span>' + row[1] + '</span></li>';
      });
    });
    this.out.spec.innerHTML = html;
    this.last = r;
  };

  Estimator.prototype.send = function () {
    var r = this.last || this.calc();
    var answers = {};
    r.groups.forEach(function (g) {
      g.rows.forEach(function (row) { answers[g.title + ': ' + row[0]] = String(row[1]); });
    });
    openQuote({
      product: r.lines.join(','),
      project_type: r.project || '',
      estimate_range: r.priced ? range(r.lo, r.hi) : '',
      answers: JSON.stringify(answers),
      source: 'estimator'
    });
  };

  function init() {
    document.querySelectorAll('[data-dp-estimator]').forEach(function (el) {
      if (!el.dpEstimator) el.dpEstimator = new Estimator(el);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // Re-init when the theme editor re-renders a section.
  document.addEventListener('shopify:section:load', init);
})();
